import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BarChart3, Boxes, ChevronLeft, ChevronRight, ClipboardList, CreditCard, FileBarChart, FileText, LayoutDashboard, Menu, PackageCheck, ReceiptText, RefreshCw, Search, WalletCards, X } from "lucide-react";
import { api } from "../pages/api";
import "./Sidebar.css";

const moduleIcons = {
  1: ClipboardList, 2: CreditCard, 3: ReceiptText, 4: Boxes, 5: PackageCheck,
  6: WalletCards, 7: FileBarChart, 8: FileText, 9: BarChart3, 10: BarChart3, 11: ReceiptText,
};

const Sidebar = () => {
  const location = useLocation();
  const [forms, setForms] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);

  const loadForms = useCallback(() => {
    if (!user?.id) return setStatus("error");
    setStatus("loading");
    api.get("/api/forms/getuserforms")
      .then(response => { setForms(response.data || []); setStatus("ready"); })
      .catch(() => setStatus("error"));
  }, [user?.id]);

  useEffect(() => { loadForms(); }, [loadForms]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { localStorage.setItem("sidebarCollapsed", String(isCollapsed)); }, [isCollapsed]);

  const visibleForms = forms.filter(form => form.name.toLowerCase().includes(query.trim().toLowerCase()));
  const closeAfterNavigation = () => setMobileOpen(false);
  const showLabels = !isCollapsed || mobileOpen;

  return <>
    <button className="mobile-menu-trigger" type="button" onClick={()=>setMobileOpen(true)} aria-label="Open application menu"><Menu size={22}/></button>
    {mobileOpen&&<button className="sidebar-backdrop" aria-label="Close application menu" onClick={()=>setMobileOpen(false)}/>} 
    <nav className={`sidebar ${isCollapsed?"collapsed":""} ${mobileOpen?"mobile-open":""}`} aria-label="Application navigation">
      <div className="sidebar-brand"><span className="sidebar-brand-mark">MD</span>{showLabels&&<span><strong>MD Workspace</strong><small>Operations portal</small></span>}<button type="button" className="sidebar-mobile-close" onClick={()=>setMobileOpen(false)} aria-label="Close menu"><X size={20}/></button></div>
      <button type="button" className="sidebar-toggle" onClick={()=>setIsCollapsed(value=>!value)} aria-label={isCollapsed?"Expand navigation":"Collapse navigation"} aria-expanded={!isCollapsed}>{isCollapsed?<ChevronRight size={18}/>:<ChevronLeft size={18}/>}</button>
      {showLabels&&<div className="sidebar-search"><Search size={16}/><input value={query} onChange={event=>setQuery(event.target.value)} placeholder="Find a module" aria-label="Find a module"/>{query&&<button type="button" onClick={()=>setQuery("")} aria-label="Clear search"><X size={14}/></button>}</div>}
      <div className="sidebar-section-label">{showLabels?"Workspace":""}</div>
      <ul className="sidebar-list">
        <li><NavLink to="/dashboard" onClick={closeAfterNavigation} title="Dashboard"><LayoutDashboard size={19}/>{showLabels&&<span>Dashboard</span>}</NavLink></li>
      </ul>
      <div className="sidebar-section-label">{showLabels?"Assigned modules":""}</div>
      <ul className="sidebar-list sidebar-modules">
        {status==="loading"?<li className="sidebar-status"><span className="sidebar-loader"/>{showLabels&&"Loading modules..."}</li>:status==="error"?<li className="sidebar-status sidebar-error"><span>Unable to load modules</span>{showLabels&&<button onClick={loadForms} type="button"><RefreshCw size={14}/> Retry</button>}</li>:visibleForms.length?visibleForms.map(form=>{const Icon=moduleIcons[form.form_id]||FileText;return <li key={`${form.form_id}-${form.id}`}><NavLink to={`/form/${form.form_id}`} onClick={closeAfterNavigation} title={form.name}><Icon size={19}/>{showLabels&&<span>{form.name}</span>}</NavLink></li>}):<li className="sidebar-status">{query?"No matching modules":"No modules assigned"}</li>}
      </ul>
      <div className="sidebar-profile" title={user.name}><span>{String(user.name||"U").charAt(0).toUpperCase()}</span>{showLabels&&<p><strong>{user.name||"User"}</strong><small>{forms.length} assigned modules</small></p>}</div>
    </nav>
  </>;
};

export default Sidebar;
